import { useEffect, useState } from 'react';
import { leaguesAPI } from '../services/apiService';

/**
 * useAssignmentLeagueConfig — THE league-config source for position-assignment
 * surfaces (DD-FIXES-0705 Fix 2, Bug 2 root-cause closure).
 *
 * Returns the CANONICAL league object from getLeagueDetails — league-shape per
 * Bible §5: `league_settings` / `roster_settings` / `salary_settings` nested
 * JSONB dicts (merged server-side by league_service.merge_complete_settings).
 *
 * Contract (NOTHING IS FLATTENED — owner directive, DD-FIXES-0705):
 *  - The returned object is passed through AS-IS. No hand-rolled shapes, no
 *    spreading settings to the top level, no flat `position_requirements`.
 *    Bug 2 WAS a caller hand-building a flat league object
 *    (BUG_2_MODAL_ADD_INVESTIGATION_20260705.md §1 step 3).
 *  - Fail loud (Rule 9): if the fetch fails or the league arrives without a
 *    non-empty `roster_settings.position_requirements`, `league` stays null
 *    and `error` is set. There is NO fallback to a parent prop, a cached
 *    object, or a rebuilt shape — a config failure must present as a config
 *    failure, never as a roster condition.
 *
 * @param {string|null} leagueId
 * @returns {{ league: object|null, error: string|null, loading: boolean }}
 */
export const useAssignmentLeagueConfig = (leagueId) => {
  const [league, setLeague] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!leagueId) {
      setLeague(null);
      setError(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const resp = await leaguesAPI.getLeagueDetails(leagueId);
        const canonical = resp?.league;
        const positionRequirements = canonical?.roster_settings?.position_requirements;
        if (!positionRequirements || Object.keys(positionRequirements).length === 0) {
          throw new Error(
            `League details for ${leagueId} are missing roster_settings.position_requirements — ` +
            'slot availability cannot be computed (canonical league shape, Bible §5)'
          );
        }
        if (!cancelled) setLeague(canonical);
      } catch (err) {
        console.error('useAssignmentLeagueConfig: league config unavailable:', err);
        if (!cancelled) {
          setLeague(null);
          setError(err?.message || 'Failed to load league configuration');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [leagueId]);

  return { league, error, loading };
};

export default useAssignmentLeagueConfig;
